import { map, type Observable } from 'rxjs';
import GatewayBase from '~/support/services/gateway-base';
import type { BrandType, DeploymentType } from '@monolog/shared/types/database/dispatch-svc';
import type { AuthMethod as ShopifyAuthMethod } from '@monolog/shared/types/database/shopify-svc';
import type { OpProductSyncOptions, OpProductSyncResult } from '@monolog/shared/types';

/** Apply-side options; `dryRun` is owned by the route, not the caller. */
export type OpProductSyncRequest = Omit<OpProductSyncOptions, 'dryRun'>;
import { WidgetType, WidgetStatus } from '~/types/widget';
import type { InfoModalFormValues } from '~/types/widget';

export type ScopeStatus = { isValid: boolean; missingScopes: string[]; authMethod: ShopifyAuthMethod | null };

export interface Brand {
  id: string;
  name: string;
  clientId: string;
  url: string;
  storeId?: string | null;
  currentVersion: string | null;
  currentCartVersion: string | null;
  currentSubscriptionsVersion: string | null;
  brandType?: BrandType;
  deployments?: any[];
  isPriority: boolean;
  // Deployment types with a live deployment — used to suppress a duplicate fresh-install prompt
  // when onboarding has already seeded one or the type is already deployed/in progress.
  liveDeploymentTypes?: DeploymentType[];
  scopeStatus?: ScopeStatus | null;
  // Transient UI flag set client-side while a brand's scope check is in flight.
  scopeLoading?: boolean;
}

export interface ThemeAuditCheck {
  name: string;
  label: string;
  passed: boolean;
  detail?: string;
  suggestion?: string;
}

export interface ThemeAuditResult {
  healthy: boolean;
  themeId: string;
  themeName: string;
  cdnIdentifier?: string;
  checks: ThemeAuditCheck[];
}

export const THEME_RUN_OPERATION = {
  AUDIT: 'AUDIT',
  IMPLEMENT: 'IMPLEMENT',
  REMOVE: 'REMOVE',
} as const;
export type ThemeRunOperation = (typeof THEME_RUN_OPERATION)[keyof typeof THEME_RUN_OPERATION];

export const THEME_RUN_STATUS = {
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
} as const;
export type ThemeRunStatus = (typeof THEME_RUN_STATUS)[keyof typeof THEME_RUN_STATUS];

/** Statuses for which a theme run is still in flight (poll + show the live cursor). */
export const ACTIVE_THEME_RUN_STATUSES: ThemeRunStatus[] = [THEME_RUN_STATUS.PENDING, THEME_RUN_STATUS.RUNNING];

export interface ThemeRun {
  id: string;
  brandId: string;
  storeId: string | null;
  operation: ThemeRunOperation;
  status: ThemeRunStatus;
  triggeredByName: string;
  draftThemeId: string | null;
  draftThemeName: string | null;
  summary: string | null;
  progress: { tool: string }[] | null;
  errorMessage: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  cacheTokens: number | null;
  createdAt: string;
  updatedAt: string;
}

export class ImplementationRestService extends GatewayBase {
  get brands() {
    return {
      audit: this.auditBrand,
      create: this.createBrand,
      delete: this.deleteBrand,
      deployments: {
        list: this.listBrandDeployments,
      },
      get: this.getBrand,
      getName: this.lookupBrand,
      list: this.listBrands,
      opProduct: {
        report: this.getBrandOpProduct,
        sync: this.syncBrandOpProduct,
      },
      themeChanges: {
        delete: this.deleteBrandThemeChange,
        download: this.downloadBrandThemeChange,
        list: this.listBrandThemeChanges,
      },
      themeRuns: {
        create: this.createThemeRun,
        list: this.listThemeRuns,
      },
      update: this.updateBrand,
      validateScopes: this.validateBrandScopes,
    };
  }

  get themeRuns() {
    return {
      cancel: this.cancelThemeRun,
      get: this.getThemeRun,
    };
  }

  get deployments() {
    return {
      comments: {
        create: this.createComment,
        load: this.loadComments,
      },
      create: this.createDeployment,
      get: this.getDeployment,
      list: this.listDeployments,
      review: {
        create: this.createReview,
      },
      start: this.startDeployment,
    };
  }

  get versions() {
    return {
      latest: this.getLatestVersions,
      listAll: this.listVersionTags,
    };
  }

  get widget() {
    return {
      create: this.createWidgetConfig,
      delete: this.deleteWidgetConfig,
      get: this.getWidgetConfig,
      getAll: this.getAllWidgetConfigs,
      getById: this.getWidgetById,
      update: this.updateWidgetConfig,
    };
  }

  get partner() {
    return {
      createApp: this.createPartnerApp,
      getJobStatus: this.getPartnerAppJobStatus,
    };
  }

  get infoModal() {
    return {
      create: this.createInfoModalConfig,
      delete: this.deleteInfoModalConfig,
      getAll: this.getAllInfoModalConfigs,
      getById: this.getInfoModalById,
      update: this.updateInfoModalConfig,
    };
  }

  private createComment = (
    id: string,
    body: { message: string; authorName: string; authorImage: string; authorId: string },
  ) => {
    return this.client.post(`/v1/implementation/deployments/${id}/comments`, body);
  };

  private loadComments = (id: string) => {
    return this.client.get<
      {
        id: string;
        message: string;
        authorName: string;
        authorImage: string;
        authorId: string;
        createdAt: string;
      }[]
    >(`/v1/implementation/deployments/${id}/comments`);
  };

  private listBrandDeployments = (id: string) => {
    return this.client.get<
      {
        oldVersion: string;
        newVersion: string;
        deployment: {
          id: string;
          name: string;
          description?: string;
          createdAt: string;
          updatedAt: string;
          approvalStatus: string;
          status: string;
          authorName: string;
          authorId: string;
        };
      }[]
    >(`/v1/implementation/brands/${id}/deployments`);
  };

  private auditBrand = (id: string, storeId?: string | null) => {
    // Pass storeId as a query param so dispatch-svc's EntitlementsGuard can
    // evaluate `implementation:read` against the actual store the user might
    // be entitled to. Without it the guard falls back to `request.params['id']`
    // — which is the brand UUID, not a storeId — and store-scoped users 401.
    const query = storeId ? `?storeId=${encodeURIComponent(storeId)}` : '';

    return this.client.get<ThemeAuditResult>(`/v1/implementation/brands/${id}/audit${query}`);
  };

  private listBrandThemeChanges = (id: string) => {
    return this.client.get<
      {
        basename: string;
        themeSlug: string | null;
        date: string | null;
        action: string | null;
        index: number | null;
        htmlUrl: string | null;
        pdfUrl: string | null;
        sizeHtml: number | null;
        sizePdf: number | null;
        createdAt: string | null;
      }[]
    >(`/v1/implementation/brands/${id}/theme-changes`);
  };

  private getBrandOpProduct = (id: string) => {
    return this.client.get<OpProductSyncResult>(`/v1/implementation/brands/${id}/op-product`);
  };

  private syncBrandOpProduct = (id: string, body: OpProductSyncRequest = {}) => {
    return this.client.post<OpProductSyncResult>(`/v1/implementation/brands/${id}/op-product/sync`, body);
  };

  private deleteBrandThemeChange = (id: string, basename: string) => {
    return this.client.delete<{ ok: true }>(
      `/v1/implementation/brands/${id}/theme-changes?basename=${encodeURIComponent(basename)}`,
    );
  };

  private downloadBrandThemeChange = (id: string, basename: string): Observable<Blob> => {
    // Use the uncached `request` (not `get`) so a repeat download doesn't replay a cached blob and
    // fire twice. The endpoint streams a raw zip, so the Axios response's `data` is the Blob.
    return this.client
      .request<Blob>({
        method: 'get',
        responseType: 'blob',
        url: `/v1/implementation/brands/${id}/theme-changes/download?basename=${encodeURIComponent(basename)}`,
      })
      .pipe(map(res => res.data));
  };

  private createThemeRun = (id: string, operation: ThemeRunOperation) => {
    return this.client.post<ThemeRun>(`/v1/implementation/brands/${id}/theme-runs`, { operation });
  };

  private listThemeRuns = (id: string) => {
    return this.client.get<ThemeRun[]>(`/v1/implementation/brands/${id}/theme-runs`);
  };

  private getThemeRun = (runId: string) => {
    return this.client.get<ThemeRun>(`/v1/implementation/theme-runs/${runId}`);
  };

  private cancelThemeRun = (runId: string) => {
    return this.client.post<ThemeRun>(`/v1/implementation/theme-runs/${runId}/cancel`, {});
  };

  private createReview = (
    id: string,
    body: {
      comment?: string;
      resolution: 'approved' | 'rejected';
      reviewerId: string;
      reviewerName: string;
    },
  ) => {
    return this.client.post(`/v1/implementation/deployments/${id}/reviews`, body);
  };

  private startDeployment = (id: string, body: { startedById: string; startedByName: string }) => {
    return this.client.post(`/v1/implementation/deployments/${id}/deploy`, body);
  };

  private listVersionTags = () => {
    return this.client.get<{
      checkout: { url: string; name: string; commit: string }[];
      cart: { url: string; name: string; commit: string }[];
      subscriptions: { url: string; name: string; commit: string }[];
    }>('/v1/implementation/version-tags');
  };

  // Latest stable version per widget type (same resolver the MCP create_deployment tool uses).
  private getLatestVersions = () => {
    return this.client.get<{ checkout: string | null; cart: string | null; subscriptions: string | null }>(
      '/v1/implementation/versions/latest',
    );
  };

  private createDeployment = (body: {
    name: string;
    version: string;
    brands: string[];
    authorId: string;
    authorName: string;
    description?: string;
    deploymentType: DeploymentType;
  }) => {
    return this.client.post<{
      id: string;
      name: string;
      version: string;
      brands: string[];
      authorId: string;
      authorName: string;
      description?: string;
    }>('/v1/implementation/deployments', body);
  };

  private listDeployments = () => {
    return this.client.get<
      {
        id: string;
        name: string;
        version: string;
        brands: { name: string; id: string; currentVersion: string }[];
        authorId: string;
        authorName: string;
        description?: string;
        reviews: {
          createdAt: string;
          authorName: string;
          status: string;
        }[];
      }[]
    >('/v1/implementation/deployments');
  };

  private getDeployment = (id: string) => {
    return this.client.get<{
      id: string;
      name: string;
      version: string;
      brands: string[];
      authorId: string;
      authorName: string;
      description?: string;
    }>(`/v1/implementation/deployments/${id}`);
  };

  private createBrand = (body: {
    name: string;
    url: string;
    clientId: string;
    identifier: string;
    brandType?: BrandType;
  }) => {
    return this.client.post<{
      id: string;
      name: string;
      clientId: string;
      deployments?: any[];
      url: string;
      identifier: string;
      brandType?: BrandType;
    }>('/v1/implementation/brands', body);
  };

  private listBrands = (storeId?: string) => {
    return this.client.get<Brand[]>(`/v1/implementation/brands${storeId ? `?storeId=${storeId}` : ''}`);
  };

  // Validates Shopify scopes for a chunk of store IDs. Called after the brands
  // list renders so scope indicators stream in progressively. Returns a
  // storeId -> ScopeStatus map; store IDs that failed validation are omitted.
  private validateBrandScopes = (storeIds: string[]) => {
    return this.client.post<Record<string, ScopeStatus>>('/v1/implementation/brands/scopes', { storeIds });
  };

  private getBrand = (id: string) => {
    return this.client.get<{
      id: string;
      clientId: string;
      name: string;
      url: string;
      identifier: string;
      currentVersion: string | null;
      currentCartVersion: string | null;
      currentSubscriptionsVersion: string | null;
      brandType?: BrandType;
      deployments?: {
        id: string;
        name: string;
        version: string;
        createdAt: string;
      }[];
    }>(`/v1/implementation/brands/${id}`);
  };

  private updateBrand = (
    id: string,
    body: {
      name: string;
      url: string;
      clientId: string;
      identifier: string;
      storeId: string;
      brandType?: BrandType;
    },
  ) => {
    return this.client.put<{
      id: string;
      clientId: string;
      name: string;
      url: string;
      identifier: string;
      storeId: string;
      brandType?: BrandType;
    }>(`/v1/implementation/brands/${id}`, body);
  };

  private deleteBrand = (id: string) => {
    return this.client.delete(`/v1/implementation/brands/${id}`);
  };
  private lookupBrand = (identifier: string, select?: string) => {
    return this.client.get<{
      name: string;
      identifier: string;
    }>(`/v1/implementation/lookup`, { params: { domain: identifier, select: 'name' } });
  };

  getWidgetConfig = (identifier: string) => {
    return this.client.get<{}>(`/v1/widget/config?id=${identifier}`);
  };

  private createWidgetConfig = (data: {
    identifier: string;
    storeId: string;
    type: WidgetType;
    config: Record<string, unknown> | unknown[];
    name?: string;
    variant?: string;
    customizationValues?: Record<string, unknown>;
  }) => {
    return this.client.post(`/v1/widget/config`, data);
  };

  private updateWidgetConfig = (
    id: string,
    config: Record<string, unknown> | unknown[],
    status?: WidgetStatus,
    customizations?: Record<string, unknown>,
    storeDomain?: string,
  ) => {
    return this.client.put(`/v1/widget/config?id=${id}&storeDomain=${storeDomain}`, { config, customizations, status });
  };

  private getWidgetById = (id: string) => {
    return this.client.get<{
      id: string;
      identifier: string;
      type: WidgetType;
      storeId: string;
      status: WidgetStatus;
      name?: string;
      variant?: string;
      config: any;
      customizations?: any;
      customizationValues?: any;
      createdAt: string;
      updatedAt: string;
      store?: any;
    }>(`/v1/widget/${id}`);
  };

  private getAllWidgetConfigs = (storeId: string) => {
    return this.client.get<
      {
        id: string;
        identifier: string;
        type: WidgetType;
        storeId: string;
        status: WidgetStatus;
        name?: string;
        variant?: string;
        config: Record<string, unknown> | unknown[];
        customizations?: Record<string, unknown>;
        createdAt: string;
        updatedAt: string;
      }[]
    >(`/v1/widget/configs?storeId=${storeId}`);
  };

  private deleteWidgetConfig = (id: string) => {
    return this.client.delete(`/v1/widget/config?id=${id}`);
  };

  private createInfoModalConfig = (data: {
    identifier: string;
    storeId: string;
    config: Record<string, unknown>;
    name?: string;
    customizationValues?: Partial<InfoModalFormValues>;
  }) => {
    return this.client.post(`/v1/widget/config`, { ...data, type: WidgetType.INFO_MODAL });
  };

  private updateInfoModalConfig = (
    id: string,
    config: Record<string, unknown>,
    status?: WidgetStatus,
    customizations?: Partial<InfoModalFormValues>,
    storeDomain?: string,
  ) => {
    const params = new URLSearchParams({ id });
    if (storeDomain) params.set('storeDomain', storeDomain);

    return this.client.put(`/v1/widget/config?${params.toString()}`, { config, customizations, status });
  };

  private getInfoModalById = (id: string) => {
    return this.client.get(`/v1/widget/${id}`);
  };

  private getAllInfoModalConfigs = (storeId: string) => {
    return this.client.get<
      {
        id: string;
        identifier: string;
        type: WidgetType;
        storeId: string;
        status: WidgetStatus;
        name?: string;
        config: Record<string, unknown>;
        customizations?: Partial<InfoModalFormValues>;
        createdAt: string;
        updatedAt: string;
      }[]
    >(`/v1/widget/configs?storeId=${storeId}&type=${WidgetType.INFO_MODAL}&_t=${Date.now()}`);
  };

  private deleteInfoModalConfig = (id: string) => {
    return this.client.delete(`/v1/widget/config?id=${id}`);
  };

  private createPartnerApp = (body: { appName: string; shopDomain: string }) => {
    return this.client.post<{ jobId: string }>('/v1/implementation/partner/apps', body);
  };

  private getPartnerAppJobStatus = (jobId: string) => {
    return this.client.get<{
      jobId: string;
      status: 'pending' | 'in_progress' | 'completed' | 'failed';
      appName: string;
      shopDomain: string;
      createdAt: string;
      updatedAt: string;
      result?: {
        appTitle: string;
      };
      error?: string;
    }>(`/v1/implementation/partner/apps/${jobId}/status`);
  };
}
